import { LoaderCircle, TriangleAlert, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { formatearMedioPago } from '../../utils/gastos'
import { formatearMontoPos } from '../../utils/pos'

function AnularPagoPosModal({
  abierto,
  pago,
  procesando = false,
  onCancelar,
  onConfirmar,
}) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (abierto) {
      setMotivo('')
      setError('')
    }
  }, [abierto])

  useEffect(() => {
    if (!abierto) return

    const alPresionarTecla = (event) => {
      if (event.key === 'Escape' && !procesando) {
        onCancelar?.()
      }
    }

    document.addEventListener('keydown', alPresionarTecla)

    return () => {
      document.removeEventListener('keydown', alPresionarTecla)
    }
  }, [abierto, procesando, onCancelar])

  if (!abierto || !pago) return null

  const alHacerClicFuera = (event) => {
    if (procesando) return

    if (event.target === event.currentTarget) {
      onCancelar?.()
    }
  }

  const confirmar = () => {
    const motivoLimpio = motivo.trim()

    if (!motivoLimpio) {
      setError('Debes indicar el motivo de la anulación.')
      return
    }

    setError('')
    onConfirmar(motivoLimpio)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={alHacerClicFuera}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="anular-pago-pos-titulo"
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl sm:p-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <TriangleAlert size={22} />
            </div>

            <h2
              id="anular-pago-pos-titulo"
              className="text-lg font-bold text-slate-900"
            >
              Anular pago
            </h2>
          </div>

          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            aria-label="Cerrar"
            className="shrink-0 text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <X size={22} />
          </button>
        </div>

        <p className="mt-4 text-sm text-slate-600">
          Se anulará el pago de{' '}
          <span className="font-semibold">
            {formatearMontoPos(pago.monto)}
          </span>{' '}
          registrado como{' '}
          <span className="font-semibold">
            {formatearMedioPago(pago.medio_pago)}
          </span>
          . El saldo pendiente de la venta se recalculará
          automáticamente.
        </p>

        <div className="mt-5">
          <label className="mb-2 block text-sm font-semibold text-slate-700">
            Motivo de la anulación
            <span className="ml-1 text-red-500">*</span>
          </label>

          <textarea
            value={motivo}
            onChange={(event) => setMotivo(event.target.value)}
            rows={3}
            placeholder="Explica por qué se anula este pago"
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100"
          />

          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancelar}
            disabled={procesando}
            className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={confirmar}
            disabled={procesando}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400 sm:w-auto"
          >
            {procesando && (
              <LoaderCircle
                size={18}
                className="animate-spin"
              />
            )}
            {procesando ? 'Anulando...' : 'Anular pago'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AnularPagoPosModal
